import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Camera, Star, X } from 'lucide-react';
import { GuestBottomNav } from '../../components/GuestBottomNav';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

export default function GuestRateVenuePage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const { transactionId } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [photos, setPhotos] = useState([]); // up to 3 URLs
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/venue-reviews/all-pending`, { headers });
        const match = (res.data?.items || []).find((p) => p.transaction_id === transactionId);
        setPrompt(match || null);
        if (!match) setError('This rating window has closed or already been submitted.');
      } catch (err) {
        setError(err.response?.data?.detail || 'Unable to load this rating.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [headers, transactionId]);

  const handlePhotoPick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (photos.length >= 3) {
      setError('You can attach up to 3 photos.');
      return;
    }
    setPhotoUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/venue/upload-photo`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setPhotos((prev) => [...prev, res.data.url]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Try a smaller image.');
    } finally {
      setPhotoUploading(false);
      event.target.value = '';
    }
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (rating < 1) {
      setError('Please tap at least 1 star.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${API}/venue-reviews`,
        { transaction_id: transactionId, rating, review_text: reviewText.trim(), photos },
        { headers },
      );
      navigate('/guest/ratings?submitted=1', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit your review. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell" data-testid="guest-rate-venue-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 18px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          data-testid="guest-rate-venue-back"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: 6 }}
        >
          <ArrowLeft size={22} strokeWidth={2.2} />
        </button>
        <span style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Rate your visit</span>
      </div>

      <div className="page-content" style={{ padding: '14px 20px 110px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {loading ? (
          <div data-testid="guest-rate-venue-loading" style={{ marginTop: 60, display: 'flex', justifyContent: 'center' }}>
            <div className="animate-spin" style={{ width: 36, height: 36, borderRadius: '50%', border: '4px solid rgba(112, 63, 246, 0.12)', borderTopColor: '#703FF6' }} />
          </div>
        ) : !prompt ? (
          <p data-testid="guest-rate-venue-not-found" style={{ fontFamily: FONT, fontSize: 16, color: 'var(--text-2)', textAlign: 'center', marginTop: 30 }}>
            {error || 'This rating window has closed.'}
          </p>
        ) : (
          <>
            <div data-testid="guest-rate-venue-info" style={{ background: 'var(--surface)', borderRadius: 18, border: '1px solid rgba(112, 63, 246, 0.10)', padding: '14px 16px', boxShadow: '0 4px 7px rgba(112, 63, 246, 0.10)' }}>
              <span style={{ fontFamily: FONT, fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: '#7A7590', fontWeight: 700 }}>
                You visited
              </span>
              <p style={{ margin: '4px 0 0', fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                {prompt.venue_name}
              </p>
              <p style={{ margin: '2px 0 0', fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', textTransform: 'capitalize' }}>
                {prompt.venue_category} · paid €{Number(prompt.amount_paid).toFixed(2)}
              </p>
            </div>

            <div data-testid="guest-rate-venue-stars" style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`guest-rate-star-${n}`}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}
                  >
                    <Star size={42} fill={filled ? '#F4C54E' : 'transparent'} color={filled ? '#F4C54E' : '#C9C3DA'} strokeWidth={2} />
                  </button>
                );
              })}
            </div>

            <textarea
              data-testid="guest-rate-venue-text"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share what you loved (optional)…"
              maxLength={2000}
              rows={5}
              style={{
                width: '100%',
                background: 'var(--surface)',
                border: '1px solid rgba(112, 63, 246, 0.16)',
                borderRadius: 16,
                padding: '14px 16px',
                fontFamily: FONT,
                fontSize: 15,
                color: 'var(--text)',
                resize: 'vertical',
                outline: 'none',
              }}
            />

            <div data-testid="guest-rate-venue-photos" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position: 'relative', width: 86, height: 86, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(112, 63, 246, 0.16)' }}>
                  <img src={p.startsWith('http') ? p : `${BACKEND_URL}${p}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    aria-label="Remove photo"
                    data-testid={`guest-rate-remove-photo-${i}`}
                    style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#FFFFFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <X size={13} strokeWidth={2.4} />
                  </button>
                </div>
              ))}
              {photos.length < 3 ? (
                <label
                  data-testid="guest-rate-add-photo"
                  style={{
                    width: 86,
                    height: 86,
                    borderRadius: 14,
                    border: '1.5px dashed rgba(112, 63, 246, 0.30)',
                    background: 'var(--surface)',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#703FF6',
                    fontFamily: FONT,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    gap: 4,
                  }}
                >
                  <Camera size={20} strokeWidth={2.2} />
                  {photoUploading ? 'Uploading…' : `Add (${photos.length}/3)`}
                  <input type="file" accept="image/*" onChange={handlePhotoPick} style={{ display: 'none' }} />
                </label>
              ) : null}
            </div>

            {error ? (
              <p data-testid="guest-rate-venue-error" style={{ margin: 0, fontFamily: FONT, fontSize: 13, color: '#C62828' }}>
                {error}
              </p>
            ) : null}

            <button
              type="button"
              data-testid="guest-rate-venue-submit"
              onClick={submit}
              disabled={submitting || rating < 1}
              style={{
                width: '100%',
                borderRadius: 9999,
                border: 'none',
                background: rating < 1 ? '#A8A4BA' : '#1A1226',
                color: '#FFFFFF',
                fontFamily: FONT,
                fontSize: 16,
                fontWeight: 700,
                padding: '13px 18px',
                cursor: submitting ? 'wait' : (rating < 1 ? 'not-allowed' : 'pointer'),
                opacity: submitting ? 0.7 : 1,
                boxShadow: '0 8px 18px rgba(26, 18, 38, 0.20)',
              }}
            >
              {submitting ? 'Submitting…' : 'Publish review'}
            </button>
          </>
        )}
      </div>

      <GuestBottomNav />
    </div>
  );
}
